import {css, StyleSheet} from 'aphrodite';
import React, {useMemo} from 'react';

import {
  BenchlingExpressionFunction,
  BenchlingExpressionType,
  BenchlingExpressionFunctionParameter,
} from 'benchling-expressions/BenchlingExpressionTypes';
import {BenchlingFunctionSignature} from 'benchling-expressions/BenchlingFunctionSignatures';
import BenchlingConfig from 'BenchlingConfig';
import {UNCONFIGURED_DEFAULT_ROUTER_FUNCTION_PARAMETER} from 'processes/modals/router-config/RouterConfigConstants';
import {FONT_WEIGHT_PROXIMA_REGULAR} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import Button, {ButtonStyle, ButtonSize} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import {DeepPartial} from 'util/Types';

export interface AddConditionButtonProps {
  dataTestAttrs?: DataTestAttrs;
  expression: DeepPartial<BenchlingExpressionFunction> | null;
  setExpression: (newExpression: DeepPartial<BenchlingExpressionFunction>) => void;
  recursiveDepth: number;
  defaultOperator: BenchlingFunctionSignature;
  didInitialFlowchartHaveChainedRouters: boolean;
  doesCurrentFlowchartHaveChainedRouters: boolean;
}

export default function AddConditionButton(props: AddConditionButtonProps): JSX.Element | null {
  const {disabled, disabledText, useDropdown} = useMemo(() => {
    const numParams = props.expression?.parameters?.length ?? 1;
    const disabled =
      numParams >= BenchlingConfig.WORKFLOWS_MAX_CONDITIONS_PER_ROUTER ||
      (props.didInitialFlowchartHaveChainedRouters && props.doesCurrentFlowchartHaveChainedRouters);
    const useDropdown = props.recursiveDepth < 1;
    let disabledText;
    if (disabled) {
      disabledText =
        props.didInitialFlowchartHaveChainedRouters && props.doesCurrentFlowchartHaveChainedRouters
          ? 'To add conditions and condition groups, remove all chained routers in this flowchart.'
          : `You can add up to ${BenchlingConfig.WORKFLOWS_MAX_CONDITIONS_PER_ROUTER} conditions or condition groups to a router`;
    }

    return {
      disabled,
      disabledText,
      useDropdown,
    };
  }, [
    props.expression,
    props.recursiveDepth,
    props.didInitialFlowchartHaveChainedRouters,
    props.doesCurrentFlowchartHaveChainedRouters,
  ]);

  function addExpression(
    defaultExpressionParameter: DeepPartial<BenchlingExpressionFunctionParameter>
  ): void {
    props.setExpression({
      ...props.expression,
      parameters: [...(props.expression?.parameters ?? []), defaultExpressionParameter],
    });
  }

  return (
    <div {...getTestProps(props.dataTestAttrs)}>
      <Tooltip isDisabled={!disabled} text={disabledText}>
        {useDropdown ? (
          <Dropdown
            menu={
              <Menu height={Menu.HEIGHTS.SMALL} width={Menu.WIDTHS.SMALL}>
                <Option
                  onSelect={() => {
                    addExpression(UNCONFIGURED_DEFAULT_ROUTER_FUNCTION_PARAMETER);
                  }}
                >
                  Add condition
                </Option>
                <Option
                  onSelect={() => {
                    addExpression({
                      name: props.defaultOperator.parameters[0].spreadParameterName,
                      expression: {
                        type: BenchlingExpressionType.FUNCTION,
                        name: props.defaultOperator.functionName,
                        parameters: [UNCONFIGURED_DEFAULT_ROUTER_FUNCTION_PARAMETER],
                      },
                    });
                  }}
                >
                  Add condition group
                </Option>
              </Menu>
            }
          >
            <Button
              buttonStyle={ButtonStyle.TEXT}
              size={ButtonSize.SMALL}
              disabled={disabled}
              isDropdown={true}
              flexWidth={true}
              className={css(styles.buttonStyle)}
              fontWeight={FONT_WEIGHT_PROXIMA_REGULAR}
              icon="compose"
              text="Add condition"
            />
          </Dropdown>
        ) : (
          <Button
            buttonStyle={ButtonStyle.TEXT}
            size={ButtonSize.SMALL}
            disabled={disabled}
            onClick={() => {
              addExpression(UNCONFIGURED_DEFAULT_ROUTER_FUNCTION_PARAMETER);
            }}
            flexWidth={true}
            className={css(styles.buttonStyle)}
            fontWeight={FONT_WEIGHT_PROXIMA_REGULAR}
            icon="compose"
            text="Add condition"
          />
        )}
      </Tooltip>
    </div>
  );
}

const styles = StyleSheet.create({
  buttonStyle: {
    padding: 0,
  },
});
